import { Text } from "@certn/cascadia";
import {
  Card,
  Container,
  DataDisplay,
  LinkButton,
} from "@certn/cascadia-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";

import { MismatchModal } from "./MismatchModal";
import { DateVarianceModal } from "./DateVarianceModal";
import { UnverifiedEmploymentModal } from "./UnverifiedEmploymentModal";
import {
  AdjudicationStatus,
  AdjudicationStatusTag,
} from "../../../utils/renderStatusTag";
import {
  getDateVarianceScore,
  getMismatchScore,
  getUnverifiedEmploymentScore,
} from "../utils";
import {
  AdjudicationFormValues,
  EmploymentVerificationCriteria,
} from "../types/form-values";

export function EmploymentVerifAdjudicationCriteria({
  editMode = false,
}: {
  editMode?: boolean;
}) {
  const { t } = useTranslation();
  const [isMismatchModalOpen, setMismatchModalOpen] = useState(false);
  const [isDateVarianceModalOpen, setDateVarianceModalOpen] = useState(false);
  const [isUnverifiedEmploymentModalOpen, setUnverifiedEmploymentModalOpen] =
    useState(false);

  const { values, setFieldValue } = useFormikContext<AdjudicationFormValues>();

  const criteria = values.criteria.find(
    (c): c is EmploymentVerificationCriteria =>
      c.check_type === "EMPLOYMENT_VERIFICATIONS_1"
  );

  const ruleset = criteria?.ruleset;

  const handleSave = (ModalValues: Record<string, unknown>, type: string) => {
    if (!criteria) {
      throw new Error("Employment Verifications criteria not found");
    }

    const criteriaIndex = values.criteria.findIndex(
      (c) => c.check_type === "EMPLOYMENT_VERIFICATIONS_1"
    );

    setFieldValue(`criteria[${criteriaIndex}].ruleset.${type}`, ModalValues);
    setFieldValue(`criteria[${criteriaIndex}].set`, true);
  };

  return (
    <Container layout="narrow">
      <Text style="global/title-small">
        {editMode ? t("Edit rules:") : t("Create rules:")}{" "}
        {t("Employment Verifications")}
      </Text>
      <Card padding="space-4">
        <DataDisplay
          columns={[
            {
              key: "rule",
              name: t("Rule"),
              isRowHeader: true,
              render: ({ rule }) => <Text style="input/helper">{rule}</Text>,
            },
            {
              key: "Score",
              name: t("Score"),
              isRowHeader: true,
              render: ({ score }) =>
                AdjudicationStatusTag(score as AdjudicationStatus),
            },
            {
              key: "edit",
              isRowHeader: true,
              render: ({ key }) => (
                <LinkButton
                  onPress={() => {
                    switch (key) {
                      case "mismatch-count":
                        setMismatchModalOpen(true);
                        break;
                      case "date-variance":
                        setDateVarianceModalOpen(true);
                        break;
                      case "unverified-employment-count":
                        setUnverifiedEmploymentModalOpen(true);
                        break;
                      default:
                        break;
                    }
                  }}
                  linkStyle="monochrome"
                  testID="edit-button"
                >
                  {t("Edit")}
                </LinkButton>
              ),
            },
          ]}
          rows={[
            {
              key: "mismatch-count",
              rule: t("Mismatch Count"),
              score: getMismatchScore(ruleset),
            },
            {
              key: "date-variance",
              rule: t("Date Variance (start/end dates)"),
              score: getDateVarianceScore(ruleset),
            },
            {
              key: "unverified-employment-count",
              rule: t("Unverified Employment Count"),
              score: getUnverifiedEmploymentScore(ruleset),
            },
          ]}
        />
      </Card>

      <MismatchModal
        isOpen={isMismatchModalOpen}
        onClose={() => setMismatchModalOpen(false)}
        onSave={(selectedValues, type) => handleSave(selectedValues, type)}
        mismatchCountData={criteria?.ruleset.mismatch_count}
      />
      <DateVarianceModal
        isOpen={isDateVarianceModalOpen}
        onClose={() => setDateVarianceModalOpen(false)}
        onSave={(selectedValues, type) => handleSave(selectedValues, type)}
        dateVarianceData={criteria?.ruleset.date_variance}
      />
      <UnverifiedEmploymentModal
        isOpen={isUnverifiedEmploymentModalOpen}
        onClose={() => setUnverifiedEmploymentModalOpen(false)}
        onSave={(selectedValues, type) => handleSave(selectedValues, type)}
        unverifiedEmploymentCountData={
          criteria?.ruleset.unverified_employment_count
        }
      />
    </Container>
  );
}
